import type { EditorState } from '@codemirror/state';

import type { FilterFieldNodeBase } from '../types/tree-nodes.js';
import { getRangeWithinDocument } from './get-range-within-document.js';

/**
 * Clean up logical operators when a statement is deleted.
 * @internal
 */
export function cleanUpInput(
  editorState: EditorState,
  deletedRange: FilterFieldNodeBase['range'],
): FilterFieldNodeBase['range'] {
  const { from, to } = getRangeWithinDocument(
    deletedRange,
    editorState.doc.length,
  );

  if (from === to) {
    return { from, to };
  }

  const fullText = editorState.doc.toString();

  // Get text before and after the deletion.
  const textBeforeDeletedStatement = fullText.substring(0, from);
  const textAfterDeletedStatement = fullText.substring(to);

  // Match optional operator and leading / trailing whitespaces: \s+(?:(AND|OR)\s*)?
  // Capture the logical operator if present.
  const matchBefore =
    /(?<spacesBefore>\s+)(?:(?<logicalOperator>AND|OR)(?<spacesAfter>\s*))?$/di.exec(
      textBeforeDeletedStatement,
    );
  const matchAfter =
    /^(?:(?<spacesBefore>\s*)(?<logicalOperator>AND|OR))?(?<spacesAfter>\s+)/di.exec(
      textAfterDeletedStatement,
    );

  const { logicalOperator: matchBefore_logicalOperator } =
    matchBefore?.groups ?? {};
  const { logicalOperator: matchAfter_logicalOperator } =
    matchAfter?.groups ?? {};

  const {
    spacesBefore: matchBefore_spacesBeforeIndices,
    logicalOperator: matchBefore_logicalOperatorIndices,
  } = matchBefore?.indices?.groups ?? {};

  const {
    spacesBefore: matchAfter_spacesBeforeIndices,
    spacesAfter: matchAfter_spacesAfterIndices,
    logicalOperator: matchAfter_logicalOperatorIndices,
  } = matchAfter?.indices?.groups ?? {};

  let startReplacingAt = matchBefore_spacesBeforeIndices?.[0] ?? from;
  let endReplacingAt =
    (matchAfter_spacesBeforeIndices?.[1] ??
      matchAfter_spacesAfterIndices?.[1] ??
      0) + to;

  // Determine which logical operator to keep if there is a preceding and succeeding logical operator.
  if (matchBefore_logicalOperator && matchAfter_logicalOperator) {
    const logicalOperatorBefore = matchBefore_logicalOperator.toUpperCase();
    const logicalOperatorAfter = matchAfter_logicalOperator.toUpperCase();

    // If the same logical operator is used before and after the deleted statement, it doesn't matter which one we remove
    // to keep only one. In this case or if the first one is a logical OR, we remove the succeeding logical operator.
    if (
      logicalOperatorBefore === logicalOperatorAfter ||
      logicalOperatorBefore === 'OR'
    ) {
      startReplacingAt = matchBefore_logicalOperatorIndices[1] + 1;
      endReplacingAt =
        (matchAfter_spacesAfterIndices?.[1] ??
          matchAfter_logicalOperatorIndices[1]) + to;
      // The preceeding logical operator is an OR, we remove the succeeding operator.
    } else {
      endReplacingAt = matchAfter_logicalOperatorIndices[0] - 1 + to;
    }
  } else if (matchBefore_logicalOperator) {
    startReplacingAt = matchBefore_logicalOperatorIndices[1] + 1;
  } else if (matchAfter_logicalOperator) {
    endReplacingAt = to;
    // There's neither a preceeding nor a succeeding logical operator.
  } else {
    startReplacingAt = matchBefore_spacesBeforeIndices
      ? matchBefore_spacesBeforeIndices[0] + 1
      : from;
    endReplacingAt =
      matchBefore !== null
        ? (matchAfter_spacesAfterIndices?.[1] ?? 0) + to
        : to;

    // Removing the very first statement. To have the cursor end up at index 0, we need to shift the replaced range by 1.
    if (
      matchBefore_spacesBeforeIndices?.[0] === 0 &&
      textAfterDeletedStatement !== ''
    ) {
      startReplacingAt -= 1;
      endReplacingAt -= 1;
    }
  }

  const newText = `${fullText.slice(0, startReplacingAt)}${fullText.slice(
    endReplacingAt,
  )}`;

  // Remove everything if only whitespaces are left after deleting the statement.
  if (newText.trim() === '') {
    return { from: 0, to: fullText.length };
  }

  return { from: startReplacingAt, to: endReplacingAt };
}
